import { AddIcon } from "@chakra-ui/icons";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Stack,
  Text,
} from "@chakra-ui/react";
import { TuteriaQuizPage } from "../tutor-revamp/quizzes/Quiz";
// import getYouTubeID from "get-youtube-id";
import { observer } from "mobx-react-lite";
import React from "react";
import { OverlayRouter, OverlayWrapper } from "../components/OverlayRouter";
import { ISubjectStore, ITutorSubject } from "../stores";
import { SUBJECT_EDIT_STEPS } from "../stores/subject";
import { useOverlayDisclosureWithCrisp } from "../utils/hooks";
import { CustomPopupDisplay, displayToast } from "./forms/SharedComponents";
import SubjectDescriptionForm from "./forms/SubjectDescriptionForm";
import { GenericSubjectCardGroup, SubjectCardGroup } from "./SubjectCardGroup";
import { SubjectPrompt } from "./SubjectPrompt";
import { HideAndShowWrapper } from "./ArchiveSubjectEditForm";
import { EmptySubject } from "./EmptySubject";

const orderSubjectsByStatus = (subjects: any) => {
  let result: any = {};
  const subjectStatus = [
    "active",
    "pending",
    "in-progress",
    "not-started",
    "denied",
  ];

  let orderedkeys = subjectStatus.filter((status) =>
    Object.keys(subjects).includes(status)
  );

  orderedkeys.forEach((key) => {
    result[key] = subjects[key];
  });

  return result;
};

const groupSubjectsByStatus = (subjects: ITutorSubject[]): any => {
  let result: any = {};
  subjects.forEach(({ status }) => {
    if (!Object.keys(result).includes(status)) {
      result[status] = subjects.filter((subject) => subject.status === status);
    }
  });
  return orderSubjectsByStatus(result);
};

export const EditSubjectWrapper: React.FC<{
  store: ISubjectStore;
  canAddSubject?: boolean;
  children?: React.ReactNode;
  triggerWelcomeModal?: boolean;
  educationAndWorkHistoryData?: any;
  getPriceSuggestion?: (subject: ITutorSubject) => any;
  renderPreview?: (data: any) => any;
  toggleNavigation?: (value: boolean) => any;
  isLiteTutor?: boolean;
  onSubjectAction: ({
    action,
    subject,
    subjects,
    status,
  }: {
    action: "edit" | "take_test" | "archive" | "delete" | "add" | "start";
    subject?: ITutorSubject;
    subjects?: Array<ITutorSubject>;
    status?: string;
  }) => any;
  canEditSubject?: boolean;
  generic?: boolean;
  proceedToNextStep?: React.ReactNode;
  generateDescription: (p: {
    name: string;
    subcategory: string;
    category: string;
  }) => Promise<void>;
}> = observer(
  ({
    children,
    store,
    canAddSubject,
    educationAndWorkHistoryData,
    onSubjectAction,
    isLiteTutor,
    renderPreview,
    getPriceSuggestion,
    toggleNavigation,
    canEditSubject = true,
    generic = false,
    proceedToNextStep,
    generateDescription,
  }) => {
    const [currentStep, setCurrentStep] = React.useState("");
    const [triggerOnce, setTrigger] = React.useState(false);
    const [action, setAction] = React.useState("edit");
    const { isOpen, onClose, onOpen } =
      useOverlayDisclosureWithCrisp("/next-action");
    const subjectsByStatus = groupSubjectsByStatus(store.tutorSubjects);
    let subjectMap = Object.entries(subjectsByStatus);
    if (generic) {
      subjectMap = [["pending", store.subjectsRequiredForJob]];
    }

    const activeSubjects = subjectsByStatus["active"] || [];

    const incompleteSubjects = store.tutorSubjects.filter((subject) =>
      ["not-started", "in-progress"].includes(subject.status)
    );

    return (
      <Box mx={{ base: -5, md: 0 }}>
        <OverlayWrapper exact={true} path={"/"}>
          <Stack spacing={6}>
            {incompleteSubjects.length && (
              <Box pb={3} px={{ base: 5, md: 0 }}>
                <SubjectPrompt
                  incompleteSubjects={incompleteSubjects}
                  cantAddMoreSubjects={!store.canAddMoreSubjects}
                />
              </Box>
            )}
            <Stack
              id="subject-selection"
              width="100%"
              rounded="lg"
              spacing={12}
            >
              <Box width="100%" px={{ base: 5, md: 0 }}>
                <Stack spacing={8}>
                  <Stack spacing={4}>
                    <Text
                      fontWeight="bold"
                      lineHeight="shorter"
                      fontSize={["3xl", "4xl"]}
                    >
                      Tutoring subjects
                    </Text>
                    <Text color="gray.600">
                      Add subjects to get bookings. You can add up to 10
                      subjects to your profile. The more approved subjects you
                      have, the more bookings you can get.
                    </Text>
                  </Stack>
                  {generic || !subjectMap.length ? null : (
                    <>
                      {canAddSubject && (
                        <Box>
                          <Button
                            size="lg"
                            colorScheme="blue"
                            leftIcon={<AddIcon />}
                            onClick={() => {
                              onSubjectAction({ action: "add" });
                              if (toggleNavigation) toggleNavigation(false);
                            }}
                            isDisabled={!store.canAddMoreSubjects}
                          >
                            Add a new subject
                          </Button>
                        </Box>
                      )}
                    </>
                  )}
                </Stack>
              </Box>

              {store.tutorSubjects.length ? (
                <Box>
                  <Box
                    borderWidth={{ md: "1px" }}
                    rounded={{ md: "lg" }}
                    overflow="hidden"
                  >
                    <Flex
                      align="center"
                      borderBottomWidth="1px"
                      borderTopWidth={{ base: "1px", md: 0 }}
                      px={{ base: 5, md: 8 }}
                      py={3}
                      bg="neutral.100"
                    >
                      <HStack width="full" justify="space-between">
                        <Text fontWeight="semibold" color="gray.500">
                          All your subjects
                        </Text>
                        <Text>{activeSubjects.length} active</Text>
                      </HStack>
                    </Flex>
                    <Stack
                      spacing={10}
                      py={{ base: 6, md: 8 }}
                      px={{ base: 5, md: 8 }}
                    >
                      {subjectMap.map(([status, subjects]) => {
                        console.log("status", status, generic);
                        let DisplayComponent = generic
                          ? GenericSubjectCardGroup
                          : SubjectCardGroup;
                        return (
                          <DisplayComponent
                            key={status}
                            store={store}
                            status={status}
                            subjects={subjects}
                            onDeleteSubjects={async (subjects) => {
                              await onSubjectAction({
                                action: "delete",
                                subjects,
                                status,
                              });
                            }}
                            onEdit={(subject) => {
                              onSubjectAction({ action: "edit", subject });
                              setAction("edit");
                              if (toggleNavigation) toggleNavigation(false);
                              onOpen();
                            }}
                            onTakeTest={(subject) => {
                              onSubjectAction({ action: "take_test", subject });
                              setAction("take_test");
                              setCurrentStep("take_test");
                              if (toggleNavigation) toggleNavigation(false);
                              onOpen();
                            }}
                            canEditSubject={canEditSubject}
                            canDeleteSubject={canAddSubject}
                          />
                        );
                      })}
                    </Stack>
                  </Box>
                  {proceedToNextStep && (
                    <Box px={{ base: 5, md: 0 }} pt={[3, 10]} width="full">
                      {proceedToNextStep}
                    </Box>
                  )}
                </Box>
              ) : (
                <EmptySubject
                  onClick={() => onSubjectAction({ action: "add" })}
                />
              )}
            </Stack>
          </Stack>
        </OverlayWrapper>
        {/* Subject addition/selection drawer */}
        {children}

        <CustomPopupDisplay
          path={`/next-action`}
          isOpen={isOpen}
          desktopSize="lg"
          isDesktopDrawer={true}
          displayCloseButton={currentStep === ""}
          onClose={onClose}
          closeOnOverlayClick={false}
        >
          {!triggerOnce && window.location.hash && !store.currentSubjectId
            ? (() => {
                if (!["#/select-subject"].includes(window.location.hash)) {
                  setTrigger(true);
                  window.location.hash = "";
                }
              })()
            : null}
          {store.currentSubjectId ? (
            <Box px={{ base: 5, md: 0 }}>
              {action === "edit" ? (
                <SubjectEditPage
                  isLiteTutor={isLiteTutor}
                  generateDescription={generateDescription}
                  educationAndWorkHistoryData={educationAndWorkHistoryData}
                  // onSubjectEdit={(value: string) => {
                  //   setCurrentStep(value);
                  // }}
                  // getPriceSuggestion={getPriceSuggestion}
                  subjectStore={store}
                  type="hide"
                  navigateToSubject={() => {
                    setCurrentStep("");
                    if (toggleNavigation) {
                      toggleNavigation(true);
                    }
                    onClose();
                  }}
                  store={store.currentSubject as any}
                >
                  {(currentForm: any, goBack: any): any => {
                    if (currentForm === SUBJECT_EDIT_STEPS.PREVIEW) {
                      if (renderPreview) {
                        return (
                          <>
                            <Button onClick={goBack}>Go Back</Button>
                            {renderPreview(store.currentSubject)}
                          </>
                        );
                      }
                      return null;
                    }
                  }}
                </SubjectEditPage>
              ) : (
                <TuteriaQuizPage
                  store={store.currentSubject as any}
                  subjectStore={store}
                  canTakeQuiz
                  isLiteTutor={isLiteTutor}
                  navigateToSubject={() => {
                    setCurrentStep("");
                    if (toggleNavigation) {
                      toggleNavigation(true);
                    }
                    onClose();
                  }}
                  toSubjectEditPage={() => {
                    setCurrentStep("");
                    if (toggleNavigation) {
                      toggleNavigation(true);
                    }
                    setAction("edit");
                  }}
                  subjectInfo={store.tuteriaSubjectForCurrentSubject}
                  canEditSubject={true}
                />
              )}
            </Box>
          ) : null}
        </CustomPopupDisplay>
      </Box>
    );
  }
);

const SubjectEditView: React.FC<{
  store: ITutorSubject;
  children: any;
  subjectStore: ISubjectStore;
  canSubmitSubject?: boolean;
  educationAndWorkHistoryData: any;
  navigateToSubject?: any;
  isLiteTutor?: boolean;
  generateDescription: (p: {
    name: string;
    subcategory: string;
    category: string;
  }) => any;
}> = observer(
  ({
    isLiteTutor,
    store,
    children,
    subjectStore,
    educationAndWorkHistoryData,
    navigateToSubject,
    generateDescription,
  }) => {
    const [loading, setLoading] = React.useState(false);
    return (
      <Box>
        <HideAndShowWrapper
          render={({ onBackClick }) => {
            return null;
          }}
        >
          {() => {
            const tuteriaInstance = subjectStore.getTuteriaSubject(store);
            let titleSubject = {
              name: store.name,
              subcategory: tuteriaInstance?.subcategory || "",
              category: tuteriaInstance?.category || "",
            };
            return (
              <Box
                as="main"
                justifyContent="stretch"
                pb={8}
                alignItems="stretch"
              >
                <SubjectDescriptionForm
                  TUTORDATA={educationAndWorkHistoryData}
                  titleSubject={titleSubject}
                  getDescriptionSummary={generateDescription?() =>
                    generateDescription(titleSubject)
                  :undefined}
                  loading={loading}
                  subject={store.name}
                  preferenceFields={store.preferenceQuestions(subjectStore)}
                  uploadStore={store.uploadStore}
                  onBackClick={navigateToSubject}
                  value={{ ...store }}
                  saveOnBlur={() => {}}
                  onSubmit={async (values, back = true) => {
                    try {
                      // await subjectStore.checkSpellingAndSimilarity(
                      //   values,
                      //   currentForm
                      // );
                      setLoading(true);
                      await store.onSubmit(
                        SUBJECT_EDIT_STEPS.DESCRIPTIONS,
                        values,
                        isLiteTutor
                      );
                      setLoading(false);
                      if (back) {
                        navigateToSubject();
                      }
                    } catch (error) {
                      setLoading(false);
                      console.log(error);
                      displayToast({
                        status: "error",
                        title: "",
                        duration: 4000,
                        position: "top",
                        description:
                          "Oops! There was a problem. Please try again",
                      });
                      throw error;
                    }
                  }}
                />
              </Box>
            );
          }}
        </HideAndShowWrapper>
      </Box>
    );
  }
);

const SubjectEditPage: React.FC<{
  store: ITutorSubject;
  type?: "modal" | "hide";
  navigateToSubject?: any;
  subjectStore: ISubjectStore;
  educationAndWorkHistoryData: any;
  children: any;
  isLiteTutor?: boolean;
  generateDescription: (p: {
    name: string;
    subcategory: string;
    category: string;
  }) => Promise<void>;
}> = observer(
  ({
    isLiteTutor,
    store,
    subjectStore,
    educationAndWorkHistoryData,
    children,
    type = "modal",
    navigateToSubject,
    generateDescription,
  }) => {
    const Wrapper = type === "modal" ? OverlayWrapperComponent : React.Fragment;
    return (
      <Wrapper>
        <SubjectEditView
          educationAndWorkHistoryData={educationAndWorkHistoryData}
          navigateToSubject={navigateToSubject}
          subjectStore={subjectStore}
          store={store}
          isLiteTutor={isLiteTutor}
          generateDescription={generateDescription}
        >
          {children}
        </SubjectEditView>
      </Wrapper>
    );
  }
);
const OverlayWrapperComponent = ({ children }: { children: any }) => {
  return (
    <OverlayRouter>
      <OverlayWrapper>{children}</OverlayWrapper>
    </OverlayRouter>
  );
};
export default SubjectEditPage;
